import clsx from "clsx";
import {
  addMonths,
  eachDayOfInterval,
  endOfMonth,
  format,
  startOfMonth,
  subMonths,
} from "date-fns";
import { enUS } from "date-fns/locale/en-US";
import { pl } from "date-fns/locale/pl";
import { useDebounce } from "ahooks";
import {
  LoadIndicator,
  NumberBox,
  RadioGroup,
  ScrollView,
  SelectBox,
  TagBox,
  TextBox,
} from "devextreme-react";
import { Button } from "devextreme-react/button";
import { Calendar, CalendarRef } from "devextreme-react/calendar";
import { DateBoxTypes } from "devextreme-react/date-box";
import { ButtonItem, Form, GroupItem, Item } from "devextreme-react/form";
import { Pagination } from "devextreme-react/pagination";
import { Popup, ToolbarItem } from "devextreme-react/popup";
import { confirm } from "devextreme/ui/dialog";
import notify from "../../lib/notify";
import _ from "lodash";
import { useContext, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  deleteAvailability,
  downloadAvailability,
  downloadCalendarData,
  fetchSubordinateEmployees,
  fetchWorkPlan,
  sendAvailability,
  WorkPlan,
} from "../../api/calendarData";
import { useAuth } from "../../contexts/auth";
import { useCompanyContext } from "../../contexts/company";
import { ThemeContext } from "../../theme/theme";
import { RoleEnum } from "../../types";
import { useScreenSize } from "../../utils/media-query";
import "./Availability.scss";

const currentDate = new Date();

const LoadingOverlay = () => (
  <div className="availability-loading-overlay">
    <LoadIndicator height={40} width={40} />
  </div>
);

const SkeletonRows = ({ rows = 6 }: { rows?: number }) => (
  <div className="availability-skeleton" aria-hidden="true">
    {Array.from({ length: rows }).map((_, index) => (
      <div key={index} className="availability-skeleton-row" />
    ))}
  </div>
);

export const Availability = () => {
  const calendarRef = useRef<CalendarRef>(null);

  const { t, i18n } = useTranslation();
  const [schedule, setSchedule] = useState<
    Record<string, { hours: string; dayType: number | null }>
  >({});
  const [currentMonth, setCurrentMonth] = useState<Date>(new Date());
  const [selectedDate, setSelectedDate] = useState<Date | undefined>(undefined);
  const [selectedEndDate, setSelectedEndDate] = useState<Date | undefined>(
    undefined,
  );
  const themeContext = useContext(ThemeContext);
  const { isXSmall, isSmall } = useScreenSize();
  const isMobile = isXSmall || isSmall;
  const { user } = useAuth();
  const [popupVisible, setPopupVisible] = useState(false);
  const [entries, setEntries] = useState<WorkPlan[]>([]);
  const [startTime, setStartTime] = useState<Date | undefined>(undefined);
  const [endTime, setEndTime] = useState<Date | undefined>(undefined);
  const [notes, setNotes] = useState<string>("");
  const [selectedDay, setSelectedDay] = useState<string>("");
  const [monthSelect, setMonthSelect] = useState<string>("");
  const [selectedDayInWeek, setSelectedDayInWeek] = useState<string>("");
  const [isLoading, setIsLoading] = useState(false);
  const [availabilityDataNew, setAvailabilityDataNew] =
    useState<boolean>(false);
  const [availabilityData, setAvailabilityData] = useState<any[]>([]);
  const [editingExistingDate, setEditingExistingDate] = useState<Date | null>(
    null,
  );
  const [timeSlots, setTimeSlots] = useState<
    { start: Date | undefined; end: Date | undefined; id?: number }[]
  >([]);
  const [excludeHolidays, setExcludeHolidays] = useState<boolean>(true);
  const [startDate, setStartDate] = useState(
    new Date(currentDate.getFullYear(), currentDate.getMonth(), 1),
  );
  const [endDate, setEndDate] = useState(
    new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0),
  );
  const role = user?.roles;

  const daysOfWeek = useMemo(
    () => [
      { id: 0, name: t("schedule.monday") },
      { id: 1, name: t("schedule.tuesday") },
      { id: 2, name: t("schedule.wednesday") },
      { id: 3, name: t("schedule.thursday") },
      { id: 4, name: t("schedule.friday") },
      { id: 5, name: t("schedule.saturday") },
      { id: 6, name: t("schedule.sunday") },
    ],
    [t],
  );

  const daysOptions = useMemo(
    () => [
      { value: "monday", label: t("schedule.monday") },
      { value: "tuesday", label: t("schedule.tuesday") },
      { value: "wednesday", label: t("schedule.wednesday") },
      { value: "thursday", label: t("schedule.thursday") },
      { value: "friday", label: t("schedule.friday") },
      { value: "saturday", label: t("schedule.saturday") },
      { value: "sunday", label: t("schedule.sunday") },
      { value: "weekday", label: t("schedule.weekday") },
      { value: "day", label: t("schedule.day") },
      { value: "holiday", label: t("schedule.holiday") },
    ],
    [t],
  );

  const monthOptions = useMemo(
    () => [
      { value: "january", label: t("schedule.january") },
      { value: "february", label: t("schedule.february") },
      { value: "march", label: t("schedule.march") },
      { value: "april", label: t("schedule.april") },
      { value: "may", label: t("schedule.may") },
      { value: "june", label: t("schedule.june") },
      { value: "july", label: t("schedule.july") },
      { value: "august", label: t("schedule.august") },
      { value: "september", label: t("schedule.september") },
      { value: "october", label: t("schedule.october") },
      { value: "november", label: t("schedule.november") },
      { value: "december", label: t("schedule.december") },
    ],
    [t],
  );

  const daysOptionsInWeek = useMemo(
    () => [
      { value: "1", label: t("1.") },
      { value: "2", label: t("2.") },
      { value: "3", label: t("3.") },
      { value: "4", label: t("4.") },
      { value: "last", label: t("schedule.last") },
    ],
    [t],
  );

  const [pageSize, setPageSize] = useState(6);
  const [pageIndex, setPageIndex] = useState(1);

  const isNotEmployee = role && !role.includes(RoleEnum.Pracownik);
  const isMajster = role && role.includes(RoleEnum.Majster);

  // 0 - etat
  // 1 - umowa cywilnoprawna (zlecenie)
  const isContractor = user?.contractType === 1;
  const isFullTime = user?.contractType === 0;
  const [viewMode, setViewMode] = useState(() =>
    user?.contractType === 0 ? "AllEmployees" : "Employee",
  );
  const translatedMonth = t(`months.${format(currentMonth, "MMMM")}`);
  const [userSend, setUserSend] = useState<number[]>([]);
  const [userSendCompanyId, setUserSendCompanyId] = useState<number[]>([]);
  const [users, setUsers] = useState<
    {
      id: number;
      companyId: number;
      code: number;
      firstName: string;
      lastName: string;
    }[]
  >([]);
  const [entriesEmployeeId, setEntriesEmployeeId] = useState<number>();
  const { companyId } = useCompanyContext();
  const [totalCount, setTotalCount] = useState(0);
  const [popupScheduleVisible, setPopupScheduleVisible] = useState(false);
  const [triggerType, setTriggerType] = useState("");
  const [triggerTypeDaily, setTriggerTypeDaily] = useState("");
  const [triggerTypeMonthly, setTriggerTypeMonthly] = useState("");
  const [triggerTypeYearly, setTriggerTypeYearly] = useState("");
  const [numericValue, setNumericValue] = useState(1);
  const [numericValue1, setNumericValue1] = useState(1);
  const [numericValue2, setNumericValue2] = useState(1);
  const [numericValue3, setNumericValue3] = useState(1);
  const [numericValue4, setNumericValue4] = useState(1);
  const [numericValue5, setNumericValue5] = useState(1);
  const [selectedDays, setSelectedDays] = useState([]);
  const lastClickTimeRef = useRef(0);
  const [onlyWeekendDays, setOnlyWeekendDays] = useState(false);
  const [searchValue, setSearchValue] = useState<string>("");
  const debouncedSearchValue = useDebounce(searchValue, { wait: 400 });

  // Indeks dostępności per pracownik+dzień - O(1) zamiast find() w każdej komórce
  const availabilityByEmployeeAndDate = useMemo(() => {
    const map = new Map<string, any>();
    availabilityData.forEach((entry) => {
      const key = `${entry.employeeId}|${new Date(
        entry.date,
      ).toLocaleDateString("en-CA")}`;
      map.set(key, entry);
    });
    return map;
  }, [availabilityData]);

  const triggerOptions = useMemo(
    () => [
      { key: "schedule.daily", label: t("schedule.daily") },
      { key: "schedule.weekly", label: t("schedule.weekly") },
      { key: "schedule.monthly", label: t("schedule.monthly") },
      { key: "schedule.yearly", label: t("schedule.yearly") },
    ],
    [t],
  );

  const triggerOptionsMonthly = useMemo(
    () => [
      { key: "schedule.monthDay", label: t("schedule.monthDay") },
      { key: "schedule.inTheNextDay", label: t("schedule.inTheNextDay") },
    ],
    [t],
  );

  const triggerOptionsDaily = useMemo(
    () => [
      { key: "schedule.what", label: t("schedule.what") },
      { key: "schedule.generalDay", label: t("schedule.generalDay") },
    ],
    [t],
  );

  const triggerOptionsYearly = useMemo(
    () => [
      { key: "schedule.data", label: t("schedule.data") },
      { key: "schedule.day", label: t("schedule.day") },
    ],
    [t],
  );

  const fetchDataForMonth = async (year: number, month: number) => {
    try {
      setIsLoading(true);
      const data = await downloadCalendarData(year, month);

      const formattedData: Record<
        string,
        { hours: string; dayType: number | null }
      > = {};

      data.data.forEach((day: any) => {
        const hours =
          day.hours
            .map(
              (hour: any) =>
                `${hour.fromHour}-${hour.toHour} (${hour.duration})`,
            )
            .join("\n") || "-";

        formattedData[day.date.split("T")[0]] = {
          hours,
          dayType: day.dayType,
        };
      });

      setSchedule(formattedData);
    } catch (error) {
      notify(`${t("Error:")} ${error || t("Unknown error")}`, "error");
    } finally {
      setIsLoading(false);
    }
  };

  const fetchDataMonthly = (date: Date, search?: string): Promise<void> => {
    if (viewMode !== "AllEmployees" || !isNotEmployee) {
      return Promise.resolve();
    }

    setIsLoading(true);

    return new Promise(async (resolve, reject) => {
      try {
        const formattedStartDate = format(
          startOfMonth(currentMonth),
          "yyyy-MM-dd",
        );
        const formattedEndDate = format(endOfMonth(currentMonth), "yyyy-MM-dd");
        const company = companyId === 0 ? user?.companyId || 0 : companyId;

        const result = await fetchWorkPlan(
          formattedStartDate,
          formattedEndDate,
          company,
          user?.employeeId || 0,
          pageIndex,
          pageSize,
          true,
          search,
        );

        if (result.isOk && result.data) {
          setEntries(result.data.items);
          setTotalCount(result.data.totalCount);
        } else {
          setEntries([]);
          setTotalCount(0);
        }

        resolve();
      } catch (error) {
        reject(error);
      } finally {
        setIsLoading(false);
      }
    });
  };

  const getEmployeeIdsForCurrentPage = () => {
    return entries.map((entry) => entry.employee.id);
  };

  const getCompanyIdsForCurrentPage = () => {
    return entries.map((entry) => entry.employee.companyId);
  };

  const fetchDataForSubordinateEmployess = async () => {
    const fetchUsers = async () => {
      try {
        let company = 0;

        if (companyId === 0) {
          company = user?.companyId || 0;
        } else {
          company = companyId;
        }
        const monthStart = new Date(
          currentMonth.getFullYear(),
          currentMonth.getMonth(),
          1,
        );
        const monthEnd = new Date(
          currentMonth.getFullYear(),
          currentMonth.getMonth() + 1,
          0,
        );
        const toIso = (d: Date) =>
          `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(
            d.getDate(),
          ).padStart(2, "0")}`;
        const response = await fetchSubordinateEmployees(
          company,
          user?.employeeId || 0,
          false,
          toIso(monthStart),
          toIso(monthEnd),
        );
        if (response.isOk) {
          setUsers(response.data);
        } else {
          const usersList = response.data.data || response.data || [];
          notify(
            `${t("Error:")} ${response.error || t("Unknown error")}`,
            "error",
          );
          setUsers(usersList);
        }
      } catch (err) {
        notify(`${t("Error:")} ${err || t("Unknown error")}`, "error");
      }
    };
    fetchUsers();
  };

  const fetchDataForAvailability = async (
    year: number,
    month: number,
    companyIds?: number[],
    employeeIds?: number[],
  ) => {
    const idsToFetch =
      employeeIds && employeeIds.length > 0
        ? employeeIds
        : user?.employeeId
          ? [user.employeeId]
          : [];

    if (idsToFetch.length === 0) {
      return;
    }

    const companyIdsToFetch =
      companyIds && companyIds.length > 0
        ? companyIds
        : user?.companyId
          ? [user.companyId]
          : [];

    if (companyIdsToFetch.length === 0) {
      return;
    }

    try {
      const response = await downloadAvailability(
        year,
        month,
        companyIdsToFetch,
        idsToFetch,
      );

      if (!response.isOk || !response.data) {
        notify(
          `${t("Error:")} ${response.error || t("Unknown error")}`,
          "error",
        );
        return;
      }

      const formattedData: any[] = [];

      response.data.forEach((employeeAvailability: any) => {
        const empId = employeeAvailability.employeeId;
        const entries = employeeAvailability.entries || [];

        entries.forEach((day: any) => {
          const realPositions = day.positions || [];
          const hasRealAvailability = realPositions.length > 0;

          const positions = hasRealAvailability
            ? realPositions
            : [
                {
                  from: "2025-02-27T08:00:00",
                  to: "2025-02-27T16:00:00",
                },
              ];

          formattedData.push({
            employeeId: empId,
            date: day.date.split("T")[0],
            available: hasRealAvailability,
            startTime: day.min || "",
            endTime: day.max || "",
            sum: day.sum || "",
            positions,
            notes: day.note || "",
            dateType: day.dateType || "",
          });
        });
      });

      setAvailabilityData(formattedData);
    } catch (error) {
      notify(`${t("Error:")} ${error || t("Unknown error")}`, "error");
    }
  };

  const changeMonth = (direction: "next" | "previous") => {
    const newMonth =
      direction === "next"
        ? addMonths(currentMonth, 1)
        : subMonths(currentMonth, 1);

    setCurrentMonth(newMonth);
  };
  const handlePageClick = (newPageIndex) => {
    setPageIndex(newPageIndex);
  };

  const handleDateClick = (
    date: Date,
    employeeId: number,
    companyId: number,
  ) => {
    setSelectedDate(date);
    setSelectedEndDate(date);
    setEntriesEmployeeId(employeeId);

    ///blokowanie dodawania zgłoszenia miesiąc wstecz - dostosować po uzgodnieniu z klientem///
    if (
      date.getFullYear() < currentDate.getFullYear() ||
      (date.getFullYear() === currentDate.getFullYear() &&
        date.getMonth() < currentDate.getMonth())
    ) {
      notify(`${t("Error:")} ${t("NotCurrentMonth")}`, "error");
      return;
    }

    // pracownicy i zleceniobiorcy mogą zgłaszać tylko siebie
    if (!isNotEmployee || isContractor || isMajster) {
      setUserSend([user?.employeeId || 0]);
      setUserSendCompanyId([user?.companyId || 0]);
    } else {
      // kierownik etatowy (AllEmployees) – może wybrać podwładnych
      setUserSend([employeeId]);
      setUserSendCompanyId([companyId]);
    }

    setPopupVisible(true);

    const formattedDate = date.toLocaleDateString("en-CA");
    const availabilityInfo = availabilityByEmployeeAndDate.get(
      `${employeeId}|${formattedDate}`,
    );

    if (!availabilityInfo || !availabilityInfo.available) {
      const defaultStart = new Date(`${formattedDate}T08:00:00`);
      const defaultEnd = new Date(`${formattedDate}T16:00:00`);

      setTimeSlots([
        {
          start: defaultStart,
          end: defaultEnd,
        },
      ]);
      setStartTime(defaultStart);
      setEndTime(defaultEnd);
      setNotes("");
      setEditingExistingDate(null);
    } else {
      const newTimeSlots = availabilityInfo.positions.map((position: any) => ({
        start: new Date(position.from),
        end: new Date(position.to),
        id: position.id,
      }));

      setTimeSlots(newTimeSlots);

      if (newTimeSlots.length > 0) {
        setStartTime(newTimeSlots[0].start);
        setEndTime(newTimeSlots[0].end);
      }

      setNotes(availabilityInfo.notes || "");
      setEditingExistingDate(date);
    }
  };

  const handleAddTimeSlot = () => {
    let newStartTime = new Date();
    if (timeSlots.length > 0) {
      const lastSlot = timeSlots[timeSlots.length - 1];
      newStartTime = new Date(lastSlot.end!);
      newStartTime.setMinutes(newStartTime.getMinutes() + 30);
    } else {
      newStartTime.setHours(8, 0, 0, 0);
    }

    const newEndTime = new Date(newStartTime);
    newEndTime.setMinutes(newEndTime.getMinutes() + 30);

    const newTimeSlot = {
      start: newStartTime,
      end: newEndTime,
    };

    setTimeSlots([...timeSlots, newTimeSlot]);
  };

  const handleRemoveTimeSlot = (index: number) => {
    const updatedTimeSlots = timeSlots.filter((_, idx) => idx !== index);
    setTimeSlots(updatedTimeSlots);
  };

  const hasTimeSlotConflict = (
    newSlot: { start: Date; end: Date },
    newIndex: number,
  ) => {
    for (let index = 0; index < timeSlots.length; index++) {
      const slot = timeSlots[index];

      if (index === newIndex) {
        continue;
      }

      if (slot.start && slot.end && newSlot.start && newSlot.end) {
        if (
          (newSlot.start >= slot.start && newSlot.start < slot.end) ||
          (newSlot.end > slot.start && newSlot.end <= slot.end) ||
          (newSlot.start <= slot.start && newSlot.end >= slot.end)
        ) {
          return true;
        }
      }
    }
    return false;
  };

  function getDatesInRange(startDateTime: Date, endDateTime: Date): Date[] {
    const dates: Date[] = [];
    const currentDate = new Date(startDateTime);

    while (currentDate <= endDateTime) {
      dates.push(new Date(currentDate));

      currentDate.setDate(currentDate.getDate() + 1);
    }

    return dates;
  }

  function getDates(startDateTime: Date, endDateTime: Date): Date[] {
    let datesInRange: Date[] = [];
    const currentDate = new Date(startDateTime);

    if (triggerType === "schedule.daily") {
      if (triggerTypeDaily === "schedule.what") {
        while (currentDate <= endDateTime) {
          datesInRange.push(new Date(currentDate));
          currentDate.setDate(currentDate.getDate() + numericValue);
        }
      } else if (triggerTypeDaily === "schedule.generalDay") {
        datesInRange = getDatesInRange(startDateTime, endDateTime);
      }
    } else if (triggerType === "schedule.weekly") {
      const selectedDaysMap: Map<number, boolean> = new Map(
        selectedDays.map((day: number) => [day, true]),
      );

      let currentWeekStart = new Date(startDateTime);
      let startDay = currentWeekStart.getDay();
      let diff = (startDay === 0 ? -6 : 1) - startDay;
      currentWeekStart.setDate(currentWeekStart.getDate() + diff);

      if (currentWeekStart < startDateTime) {
        currentWeekStart.setDate(currentWeekStart.getDate() + 7);
      }

      while (currentWeekStart <= endDateTime) {
        for (let i = 0; i < 7; i++) {
          const dateToAdd = new Date(currentWeekStart);
          dateToAdd.setDate(currentWeekStart.getDate() + i);
          if (selectedDaysMap.has((dateToAdd.getDay() + 6) % 7)) {
            if (dateToAdd >= startDateTime && dateToAdd <= endDateTime) {
              datesInRange.push(new Date(dateToAdd));
            }
          }
        }

        currentWeekStart.setDate(
          currentWeekStart.getDate() + 7 * numericValue5,
        );
      }
    } else if (triggerType === "schedule.monthly") {
      if (triggerTypeMonthly === "schedule.monthDay") {
        let tempDate = new Date(startDateTime);

        while (true) {
          let newDate = new Date(
            tempDate.getFullYear(),
            tempDate.getMonth(),
            numericValue1,
          );

          if (newDate > endDateTime) break;

          if (newDate >= startDateTime) {
            datesInRange.push(newDate);
          }

          tempDate.setMonth(tempDate.getMonth() + numericValue2);
        }
      } else if (triggerTypeMonthly === "schedule.inTheNextDay") {
        let tempDate = new Date(startDateTime);

        const daysMapping: { [key: string]: number } = {
          sunday: 0,
          monday: 1,
          tuesday: 2,
          wednesday: 3,
          thursday: 4,
          friday: 5,
          saturday: 6,
        };

        const selectedDayNumber = daysMapping[selectedDay];

        while (tempDate <= endDateTime) {
          let firstDayOfMonth = new Date(
            tempDate.getFullYear(),
            tempDate.getMonth(),
            1,
          );
          let currentDay = new Date(firstDayOfMonth);
          let weekCount = 0;

          while (currentDay.getMonth() === firstDayOfMonth.getMonth()) {
            if (currentDay.getDay() === selectedDayNumber) {
              weekCount++;

              if (
                weekCount.toString() === selectedDayInWeek ||
                (selectedDayInWeek === "last" &&
                  currentDay.getMonth() === tempDate.getMonth())
              ) {
                if (currentDay >= startDateTime && currentDay <= endDateTime) {
                  datesInRange.push(new Date(currentDay));
                }
                break;
              }
            }
            currentDay.setDate(currentDay.getDate() + 1);
          }

          tempDate.setMonth(tempDate.getMonth() + numericValue3);
        }
      }
    } else if (triggerType === "schedule.yearly") {
      if (triggerTypeYearly === "schedule.data") {
        let tempYear = startDateTime.getFullYear();
        const endYear = endDateTime.getFullYear();

        const monthMapping: { [key: string]: number } = {
          january: 0,
          february: 1,
          march: 2,
          april: 3,
          may: 4,
          june: 5,
          july: 6,
          august: 7,
          september: 8,
          october: 9,
          november: 10,
          december: 11,
        };

        const monthIndex = monthMapping[monthSelect];

        if (monthIndex !== undefined) {
          while (tempYear <= endYear) {
            let targetDate = new Date(tempYear, monthIndex, numericValue5);

            if (targetDate >= startDateTime && targetDate <= endDateTime) {
              datesInRange.push(targetDate);
            }

            tempYear += numericValue4;
          }

          let lastYearDate = new Date(endYear, monthIndex, numericValue5);
          if (lastYearDate >= startDateTime && lastYearDate <= endDateTime) {
            datesInRange.push(lastYearDate);
          }
        }
      } else if (triggerTypeYearly === "schedule.day") {
        let tempYear = startDateTime.getFullYear();
        const endYear = endDateTime.getFullYear();

        const monthMapping: { [key: string]: number } = {
          january: 0,
          february: 1,
          march: 2,
          april: 3,
          may: 4,
          june: 5,
          july: 6,
          august: 7,
          september: 8,
          october: 9,
          november: 10,
          december: 11,
        };

        const dayMapping: { [key: string]: number } = {
          sunday: 0,
          monday: 1,
          tuesday: 2,
          wednesday: 3,
          thursday: 4,
          friday: 5,
          saturday: 6,
        };

        const monthIndex = monthMapping[monthSelect];
        const selectedDayNumber = dayMapping[selectedDay];

        if (monthIndex !== undefined && selectedDayNumber !== undefined) {
          while (tempYear <= endYear) {
            let firstDayOfMonth = new Date(tempYear, monthIndex, 1);
            let weekCount = 0;
            let foundDate: Date | null = null;

            while (firstDayOfMonth.getMonth() === monthIndex) {
              if (firstDayOfMonth.getDay() === selectedDayNumber) {
                weekCount++;
                if (
                  weekCount.toString() === selectedDayInWeek ||
                  (selectedDayInWeek === "last" &&
                    firstDayOfMonth.getMonth() !== monthIndex)
                ) {
                  foundDate = new Date(firstDayOfMonth);
                  break;
                }
              }
              firstDayOfMonth.setDate(firstDayOfMonth.getDate() + 1);
            }

            if (
              foundDate &&
              foundDate >= startDateTime &&
              foundDate <= endDateTime
            ) {
              datesInRange.push(foundDate);
            }

            tempYear += numericValue4;
          }
        }
      }
    } else {
      datesInRange = getDatesInRange(startDateTime, endDateTime);
    }

    return datesInRange;
  }

  const handleAvailability = async (employeeId: number) => {
    if (!selectedDate || !selectedEndDate || !startTime || !endTime) {
      notify(t("All fields must be completed."), "error");
      return;
    }

    if (selectedDate > selectedEndDate) {
      notify(
        t("The start date cannot be greater than or equal to the end date."),
        "error",
      );
      return;
    }

    for (let index = 0; index < timeSlots.length; index++) {
      const slot = timeSlots[index];
      if (hasTimeSlotConflict({ start: slot.start!, end: slot.end! }, index)) {
        notify(t("One or more time slots overlap."), "error");
        return;
      }
    }

    setIsLoading(true);

    try {
      const startDateTime = new Date(selectedDate);
      const endDateTime = new Date(selectedEndDate || selectedDate);

      const formatTime = (date: Date): string => {
        const hours = date.getHours().toString().padStart(2, "0");
        const minutes = date.getMinutes().toString().padStart(2, "0");
        return `${hours}:${minutes}`;
      };

      const positions = timeSlots.map((slot) => {
        const from = formatTime(slot.start || new Date());
        const to = formatTime(slot.end || new Date());
        return {
          from,
          to,
          id: slot.id,
        };
      });

      const datesInRange = getDates(startDateTime, endDateTime);

      const employeeIds = userSend.length > 0 ? userSend : [employeeId];

      const company = user?.companyId || 0;
      const companyIds =
        userSendCompanyId.length > 0 ? userSendCompanyId : [company];

      const response = await sendAvailability(
        employeeIds,
        companyIds,
        datesInRange,
        excludeHolidays,
        notes,
        positions,
      );

      if (response.isOk) {
        notify(t("Availability has been successfully saved."), "success");
        setTriggerType("");
        const year = currentMonth.getFullYear();
        const month = currentMonth.getMonth() + 1;

        if (viewMode !== "AllEmployees") {
          fetchDataForAvailability(year, month, undefined, employeeIds);
        } else {
          setAvailabilityDataNew((prevState) => !prevState);
        }
      } else {
        notify(
          `${t("Error:")} ${response.error || t("Unknown error")}`,
          "error",
        );
      }
    } catch (error) {
      notify(`${t("Error:")} ${error || t("Unknown error")}`, "error");
    } finally {
      setIsLoading(false);
      setPopupVisible(false);
      setEditingExistingDate(null);
    }
    setUserSend([]);
    setUserSendCompanyId([]);
  };

  const handleDeleteAvailability = async () => {
    if (!editingExistingDate) return;

    const confirmed = await confirm(
      t("availabilityPopup.confirmDeleteAvailability"),
      t("common.confirmation"),
    );
    if (!confirmed) return;

    setIsLoading(true);
    try {
      const response = await deleteAvailability(editingExistingDate);
      if (response.isOk) {
        notify(t("availabilityPopup.availabilityDeletedToast"), "success");
        const year = currentMonth.getFullYear();
        const month = currentMonth.getMonth() + 1;
        if (viewMode !== "AllEmployees") {
          fetchDataForAvailability(year, month);
        } else {
          setAvailabilityDataNew((prevState) => !prevState);
        }
      } else {
        notify(
          `${t("Error:")} ${response.error || t("Unknown error")}`,
          "error",
        );
      }
    } catch (error) {
      notify(`${t("Error:")} ${error || t("Unknown error")}`, "error");
    } finally {
      setIsLoading(false);
      setPopupVisible(false);
      setEditingExistingDate(null);
      setUserSend([]);
      setUserSendCompanyId([]);
    }
  };

  const handleCancel = () => {
    setPopupVisible(false);
    setUserSend([]);
    setUserSendCompanyId([]);
    setTriggerType("");
    setEditingExistingDate(null);
    if (popupScheduleVisible === true) {
      setPopupScheduleVisible((prevState) => !prevState);
    }
  };

  const getClassForDay = (date, theme, employeeId, entry?: WorkPlan) => {
    const formattedDate = (
      date instanceof Date ? date : new Date(date)
    ).toLocaleDateString("en-CA");

    let dayType: number | null = null;

    if (viewMode !== "AllEmployees") {
      const { dayType: scheduleDayType } = schedule[formattedDate] || {
        hours: "-",
        dayType: null,
      };
      dayType = scheduleDayType;
    } else if (entry) {
      const dayTypeFromEntry = entry.days.find(
        (day) =>
          new Date(day.date).toLocaleDateString("en-CA") === formattedDate,
      )?.dayType;

      if (dayTypeFromEntry !== undefined) {
        dayType = dayTypeFromEntry;
      }
    }

    const availabilityInfo = availabilityByEmployeeAndDate.get(
      `${employeeId}|${formattedDate}`,
    ) || {
      available: false,
      startTime: "",
      endTime: "",
      sum: "",
      positions: [],
      dayType: "",
    };

    const formatTime = (time) => {
      const dateObj = new Date(time);
      return dateObj.toLocaleTimeString([], {
        hour: "2-digit",
        minute: "2-digit",
        hour12: false,
      });
    };

    const formatTimeWithoutSeconds = (timeString) => {
      const [hours, minutes] = timeString.split(":");
      return `${hours}:${minutes}`;
    };

    const availabilityText =
      availabilityInfo.available &&
      availabilityInfo.startTime &&
      availabilityInfo.endTime &&
      availabilityInfo.sum
        ? `* ${formatTime(availabilityInfo.startTime)}-${formatTime(
            availabilityInfo.endTime,
          )} (${formatTimeWithoutSeconds(availabilityInfo.sum)})`
        : "-";

    const changeClass = clsx({
      "dx-calendar-cell-work": dayType === 1,
      "dx-calendar-cell-free": dayType === 2,
      "dx-calendar-cell-holiday": dayType === 3,
      "dx-calendar-cell-udefind": dayType === null,
      "dx-calendar-cell-dark": theme === "dark",
    });
    const availabilityClass = availabilityInfo.available
      ? "dx-calendar-cell-available"
      : "dx-calendar-cell-not-available";

    return {
      changeclass: clsx(changeClass, availabilityClass),
      availabilityText,
    };
  };

  const handleMoreOptions = () => {
    setPopupScheduleVisible((prevState) => !prevState);

    if (popupScheduleVisible === false) {
      setTriggerType("");
    }
  };

  const handleTriggerTypeChange = (e) => {
    setTriggerType(e.value);
  };

  const handleTriggerTypeChangeDaily = (e) => {
    setTriggerTypeDaily(e.value);
  };

  const handleTriggerTypeChangeMonthly = (e) => {
    setTriggerTypeMonthly(e.value);
  };
  const handleTriggerTypeChangeYearly = (e) => {
    setTriggerTypeYearly(e.value);
  };

  const handleNumericChange = (e) => {
    setNumericValue(e.value);
  };
  const handleNumericChange1 = (e) => {
    setNumericValue1(e.value);
  };
  const handleNumericChange2 = (e) => {
    setNumericValue2(e.value);
  };
  const handleNumericChange3 = (e) => {
    setNumericValue3(e.value);
  };
  const handleNumericChange4 = (e) => {
    setNumericValue4(e.value);
  };
  const handleNumericChange5 = (e) => {
    setNumericValue5(e.value);
  };

  const handleSelectionChange = (e) => {
    setSelectedDays(e.value);
    if (popupScheduleVisible === false) {
      setSelectedDays([]);
    }
  };

  useEffect(() => {
    if (isNotEmployee) {
      fetchDataForSubordinateEmployess();
    }
  }, []);

  // jak użytkownik jest kierasm lub wyżej – ma dostęp do AllEmployees niezależnie od typu umowy dla jego
  // useEffect(() => {
  //   if (isMajster) {
  //     // Majster zawsze ma dostęp do widoku AllEmployees
  //     setViewMode("AllEmployees");
  //   } else if (isNotEmployee) {
  //     setViewMode("AllEmployees");
  //   } else {
  //     setViewMode("Employee");
  //   }
  // }, [isNotEmployee, isMajster]);

  useEffect(() => {
    if (viewMode === "AllEmployees") return;
    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth() + 1;
    fetchDataForMonth(year, month);
    fetchDataForAvailability(year, month);
    setStartDate(
      new Date(currentMonth.getFullYear(), currentMonth.getMonth(), 1),
    );
    setEndDate(
      new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 0),
    );
  }, [currentMonth, viewMode]);

  // Jedno źródło pobierania listy pracowników (wcześniej 3 osobne efekty = 3 requesty)
  useEffect(() => {
    fetchDataMonthly(currentMonth, debouncedSearchValue).catch((err) => {});
  }, [
    currentMonth,
    viewMode,
    pageIndex,
    pageSize,
    availabilityDataNew,
    debouncedSearchValue,
    companyId,
  ]);

  useEffect(() => {
    if (viewMode !== "AllEmployees") return;
    if (!entries || entries.length === 0) {
      return;
    }

    const employeeIds = getEmployeeIdsForCurrentPage();
    if (!employeeIds || employeeIds.length === 0) {
      return;
    }

    const companyIds = getCompanyIdsForCurrentPage();
    if (!companyIds || companyIds.length === 0) {
      return;
    }

    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth() + 1;

    fetchDataForAvailability(year, month, companyIds, employeeIds).catch();
  }, [entries]);

  useEffect(() => {
    if (selectedDate && selectedEndDate) {
      if (selectedDate < selectedEndDate) {
        const dates = eachDayOfInterval({
          start: selectedDate,
          end: selectedEndDate,
        });

        let weekendDays = 0;
        for (const date of dates) {
          if (date.getDay() === 6 || date.getDay() === 0) {
            weekendDays++;
          }
        }

        setOnlyWeekendDays(excludeHolidays && dates.length === weekendDays);
      } else {
        setOnlyWeekendDays(false);
      }
    }
  }, [selectedDate, selectedEndDate, excludeHolidays]);

  const todayKey = new Date().toLocaleDateString("en-CA");
  const monthKey = format(currentMonth, "yyyy-MM");

  return (
    <ScrollView useNative>
      <div className="p-6">
        <div className="mb-6 flex flex-col md:flex-row gap-2 justify-between items-start">
          <div>
            <div className="mb-2 text-2xl font-bold leading-none">
              {t("Availability")}
            </div>
            <div className="text-base">{t("availabilityPopup.headerText")}</div>
          </div>
          {isNotEmployee && (
            <SelectBox
              width={180}
              dataSource={[
                // jeżeli zalogowany użytkownik ma umowę cywilnoprawną
                ...(user.contractType === 1
                  ? [{ value: "Employee", text: t("Employee") }]
                  : []),
                { value: "AllEmployees", text: t("AllEmployees") },
              ]}
              value={viewMode}
              displayExpr="text"
              valueExpr="value"
              labelMode="static"
              label={t("view")}
              onValueChanged={(e) => setViewMode(e.value)}
            />
          )}
        </div>
        {viewMode === "Employee" && isMobile ? (
          <div className="availability-mobile availability-loading-host">
            {isLoading ? <LoadingOverlay /> : null}
              <div className="availability-mobile-header">
                <Button
                  icon="chevronprev"
                  stylingMode="text"
                  onClick={() => changeMonth("previous")}
                />
                <div
                  key={monthKey}
                  className="availability-mobile-title availability-month-title"
                >
                  {`${_.upperFirst(translatedMonth)} ${format(
                    currentMonth,
                    "yyyy",
                  )}`}
                </div>
                <Button
                  icon="chevronnext"
                  stylingMode="text"
                  onClick={() => changeMonth("next")}
                />
                <Button
                  text={i18n.language === "en" ? "Today" : "Dziś"}
                  stylingMode="outlined"
                  onClick={() => setCurrentMonth(new Date())}
                />
              </div>
              <div className="availability-mobile-legend">
                <div className="availability-mobile-legend-item">
                  <span className="availability-mobile-legend-swatch availability-mobile-legend-swatch-holiday" />
                  <span>{t("Holiday")}</span>
                </div>
                <div className="availability-mobile-legend-item">
                  <span className="availability-mobile-legend-swatch availability-mobile-legend-swatch-free" />
                  <span>{t("Day off")}</span>
                </div>
                <div className="availability-mobile-legend-item">
                  <span>{t("* Reported availability")}</span>
                </div>
              </div>
              <div
                key={monthKey}
                className="availability-mobile-list availability-month-content"
              >
                {eachDayOfInterval({ start: startDate, end: endDate }).map(
                  (day) => {
                    const formattedDate = day.toLocaleDateString("en-CA");
                    const { changeclass, availabilityText } = getClassForDay(
                      formattedDate,
                      themeContext?.theme,
                      user?.employeeId,
                    );
                    const daySchedule = schedule[formattedDate];
                    const dayShort = _.upperFirst(
                      format(day, "EEEEEE", {
                        locale: i18n.language === "en" ? enUS : pl,
                      }),
                    );
                    const todayStr = new Date().toLocaleDateString("en-CA");
                    const isToday = formattedDate === todayStr;
                    const isPastMonth =
                      day.getFullYear() < currentDate.getFullYear() ||
                      (day.getFullYear() === currentDate.getFullYear() &&
                        day.getMonth() < currentDate.getMonth());
                    return (
                      <div
                        key={formattedDate}
                        className={clsx(
                          "availability-mobile-day",
                          changeclass,
                          {
                            "availability-mobile-day-today": isToday,
                            "availability-mobile-day-past": isPastMonth,
                          },
                        )}
                        onClick={() =>
                          handleDateClick(
                            day,
                            user?.employeeId || 0,
                            user?.companyId || 0,
                          )
                        }
                      >
                        <div className="availability-mobile-day-left">
                          <div className="availability-mobile-day-num">
                            {day.getDate()}
                          </div>
                          <div className="availability-mobile-day-name">
                            {dayShort}
                          </div>
                        </div>
                        <div className="availability-mobile-day-right">
                          <div className="availability-mobile-day-hours">
                            {daySchedule?.hours && daySchedule.hours !== "-"
                              ? daySchedule.hours
                              : t("Day off")}
                          </div>
                          {availabilityText && availabilityText !== "-" && (
                            <div className="availability-mobile-day-availability">
                              {availabilityText}
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  },
                )}
              </div>
          </div>
        ) : null}
        {viewMode === "Employee" && !isMobile ? (
          <div className="weekly availability-loading-host">
            {isLoading ? <LoadingOverlay /> : null}
            <Calendar
                ref={calendarRef}
                className="calendar-data mx-auto max-w-5xl"
                defaultValue={[currentMonth]}
                firstDayOfWeek={1}
                width="100%"
                height={750}
                minZoomLevel="year"
                cellRender={({ date, text, view }) => {
                  if (view !== "month") {
                    return <span>{text}</span>;
                  }
                  const formattedDate = new Date(date).toLocaleDateString(
                    "en-CA",
                  );
                  const { changeclass, availabilityText } = getClassForDay(
                    formattedDate,
                    themeContext?.theme,
                    user?.employeeId,
                  );

                  if (date < startDate || date > endDate) {
                    return (
                      <div
                        className={`empty-cell-render ${changeclass}`}
                        style={{
                          minHeight: "100px",
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "center",
                          justifyContent: "center",
                          borderRadius: "4px",
                          fontWeight: "bold",
                        }}
                      >
                        {text}
                      </div>
                    );
                  }

                  if (view === "month") {
                    return (
                      <div
                        className={changeclass}
                        style={{
                          minHeight: "100px",
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "center",
                          justifyContent: "center",
                          borderRadius: "4px",
                        }}
                        onClick={() => {
                          const now = Date.now();
                          const timeSinceLastClick =
                            now - lastClickTimeRef.current;

                          if (timeSinceLastClick < 300) {
                            lastClickTimeRef.current = 0;
                            handleDateClick(
                              date,
                              user?.employeeId || 0,
                              user?.companyId || 0,
                            );
                          } else {
                            lastClickTimeRef.current = now;
                          }
                        }}
                      >
                        <div
                          style={{
                            marginBottom: "4px",
                            // fontSize: "15px",
                            fontWeight: "bold",
                          }}
                        >
                          {text}
                        </div>
                        <div>{schedule[formattedDate]?.hours}</div>
                        <div>{availabilityText}</div>
                      </div>
                    );
                  }

                  return (
                    <div
                      style={{
                        minHeight: "100px",
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "center",
                        borderRadius: "4px",
                        fontSize: "15px",
                        fontWeight: "bold",
                      }}
                    >
                      <div>{text}</div>
                    </div>
                  );
                }}
                disabledDates={({ date, view }) => {
                  return (
                    view === "month" && date.getMonth() !== startDate.getMonth()
                  );
                }}
                onOptionChanged={(event) => {
                  if (event.fullName === "currentDate") {
                    const currentValue = event.value as Date;
                    const previousValue = event.previousValue as Date;

                    if (currentValue.getMonth() !== previousValue.getMonth()) {
                      setCurrentMonth(currentValue);
                    }
                  }
                }}
                selectionMode="multiple"
              />
          </div>
        ) : null}
        {viewMode === "AllEmployees" ? (
          <div className="availability-loading-host">
            {isLoading && entries.length > 0 ? <LoadingOverlay /> : null}
            <>
                {isMobile ? (
                  <>
                    <div className="mb-3">
                      <TextBox
                        value={searchValue}
                        placeholder={t("presence.search")}
                        width="100%"
                        onValueChange={(value) => {
                          setSearchValue(value);
                          setPageIndex(1);
                        }}
                        showClearButton
                      />
                    </div>
                    <div className="availability-allemp-mobile-header">
                      <Button
                        icon="chevronprev"
                        stylingMode="text"
                        onClick={() => changeMonth("previous")}
                      />
                      <div
                        key={monthKey}
                        className="availability-allemp-mobile-title availability-month-title"
                      >
                        {`${_.upperFirst(translatedMonth)} ${format(
                          currentMonth,
                          "yyyy",
                        )}`}
                      </div>
                      <Button
                        icon="chevronnext"
                        stylingMode="text"
                        onClick={() => changeMonth("next")}
                      />
                    </div>
                  </>
                ) : (
                  <div className="flex justify-between items-center">
                    <div className="mb-3 flex items-center gap-1">
                      <Button
                        icon="chevronprev"
                        stylingMode="text"
                        onClick={() => changeMonth("previous")}
                      />
                      <div
                        key={monthKey}
                        className="w-32 text-center text-base availability-month-title"
                      >{`${_.upperFirst(translatedMonth)} ${format(
                        currentMonth,
                        "yyyy",
                      )}`}</div>
                      <Button
                        icon="chevronnext"
                        stylingMode="text"
                        onClick={() => changeMonth("next")}
                      />
                    </div>
                    <div className="mb-3">
                      <TextBox
                        value={searchValue}
                        placeholder={t("presence.search")}
                        width={isMobile ? "100%" : undefined}
                        onValueChange={(value) => {
                          setSearchValue(value);
                          setPageIndex(1);
                        }}
                        showClearButton
                      />
                    </div>
                  </div>
                )}
                {entries.length === 0 ? (
                  isLoading ? (
                    <SkeletonRows />
                  ) : (
                    <div
                      className="p-4 text-sm text-blue-800 rounded-lg bg-blue-100 dark:bg-gray-800 dark:text-blue-400"
                      role="alert"
                    >
                      {t("noSubordinateEmployees")}
                    </div>
                  )
                ) : isMobile ? (
                  <div
                    key={monthKey}
                    className="availability-allemp-mobile availability-month-content"
                  >
                    {entries.map((entry) => {
                      const canEdit =
                        !isMajster ||
                        (isMajster &&
                          entry.employee.id === user?.employeeId &&
                          entry.employee.companyId === user?.companyId);
                      const todayStr = new Date().toLocaleDateString("en-CA");
                      return (
                        <div
                          key={entry.employee.id}
                          className="availability-allemp-mobile-section"
                        >
                          <div className="availability-allemp-mobile-section-header">
                            <div className="availability-allemp-mobile-section-name">
                              {`${entry.employee.lastName} ${entry.employee.firstName}`}
                            </div>
                            <div className="availability-allemp-mobile-section-summary">
                              {`${t("Zaplanowano")}: ${
                                entry.sum.sumaGodzin
                              } ${t("godz.")} / ${entry.sum.sumaDni} ${t(
                                "dni",
                              )}`}
                            </div>
                          </div>
                          <div className="availability-allemp-mobile-section-days">
                            {entry.days.map((day) => {
                              const { changeclass, availabilityText } =
                                getClassForDay(
                                  day.date,
                                  themeContext?.theme,
                                  entry.employee.id,
                                  entry,
                                );
                              const dayDate = new Date(day.date);
                              const dayKey =
                                dayDate.toLocaleDateString("en-CA");
                              const dayShort = _.upperFirst(
                                format(dayDate, "EEEEEE", {
                                  locale: i18n.language === "en" ? enUS : pl,
                                }),
                              );
                              const isToday = dayKey === todayStr;
                              const isPastMonth =
                                dayDate.getFullYear() <
                                  currentDate.getFullYear() ||
                                (dayDate.getFullYear() ===
                                  currentDate.getFullYear() &&
                                  dayDate.getMonth() < currentDate.getMonth());
                              const hasHours =
                                day.hours.fromHour || day.hours.toHour;
                              const hoursText = hasHours
                                ? `${day.hours.fromHour}-${day.hours.toHour}${
                                    day.hours.duration !== ""
                                      ? ` (${day.hours.duration})`
                                      : ""
                                  }`
                                : t("Day off");
                              return (
                                <div
                                  key={day.date}
                                  className={clsx(
                                    "availability-mobile-day",
                                    changeclass,
                                    {
                                      "availability-mobile-day-today": isToday,
                                      "availability-mobile-day-past":
                                        isPastMonth,
                                      "availability-mobile-day-disabled":
                                        !canEdit,
                                    },
                                  )}
                                  onClick={() => {
                                    if (canEdit) {
                                      handleDateClick(
                                        dayDate,
                                        entry.employee.id,
                                        entry.employee.companyId,
                                      );
                                    }
                                  }}
                                >
                                  <div className="availability-mobile-day-left">
                                    <div className="availability-mobile-day-num">
                                      {dayDate.getDate()}
                                    </div>
                                    <div className="availability-mobile-day-name">
                                      {dayShort}
                                    </div>
                                  </div>
                                  <div className="availability-mobile-day-right">
                                    <div className="availability-mobile-day-hours">
                                      {hoursText}
                                    </div>
                                    {availabilityText &&
                                      availabilityText !== "-" && (
                                        <div className="availability-mobile-day-availability">
                                          {availabilityText}
                                        </div>
                                      )}
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div
                    key={monthKey}
                    className={clsx(
                      "table-wrapper availability-table availability-month-content",
                      themeContext?.theme === "dark" ? "dark" : null,
                    )}
                    style={{ maxHeight: "60vh" }}
                  >
                    <table>
                      <thead>
                        <tr
                          className="sticky"
                          style={{
                            top: 0,
                            zIndex: 3,
                            backgroundColor:
                              themeContext?.theme === "dark"
                                ? "#1e1e1e"
                                : "#f9f9f9",
                          }}
                        >
                          <th
                            className="sticky"
                            style={{
                              left: 0,
                              backgroundColor:
                                themeContext?.theme === "dark"
                                  ? "#1e1e1e"
                                  : "#f9f9f9",
                              zIndex: 4,
                            }}
                          >
                            <div className="box-dark w-32 sm:w-60">
                              {t("Pracownik")}
                            </div>
                          </th>
                          {entries[0]?.days?.map((day) => (
                            <th
                              key={day.date}
                              className={clsx({
                                "availability-today-col":
                                  new Date(day.date).toLocaleDateString(
                                    "en-CA",
                                  ) === todayKey,
                              })}
                              style={{
                                backgroundColor:
                                  themeContext?.theme === "dark"
                                    ? "#1e1e1e"
                                    : "#f9f9f9",
                              }}
                            >
                              <div
                                className="box-dark"
                                style={{ minWidth: 140, width: "100%" }}
                              >
                                {_.upperFirst(
                                  format(day.date, "EEEEEE, d.LL", {
                                    locale: i18n.language === "en" ? enUS : pl,
                                  }),
                                )}
                              </div>
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {entries.map((entry) => (
                          <tr key={entry.employee.id}>
                            <td
                              className="sticky"
                              style={{
                                height: "90px",
                                left: 0,
                                backgroundColor:
                                  themeContext?.theme === "dark"
                                    ? "#1e1e1e"
                                    : "#f9f9f9",
                                zIndex: 2,
                              }}
                            >
                              <div className="box-dark w-32 sm:w-60 h-full">
                                <div className="font-bold text-wrap truncate">
                                  {`${entry.employee.lastName} ${entry.employee.firstName}`}
                                </div>
                                <div className="text-wrap truncate">
                                  {`${t("Zaplanowano")}: ${
                                    entry.sum.sumaGodzin
                                  } ${t("godz.")} / ${entry.sum.sumaDni} ${t(
                                    "dni",
                                  )}`}
                                </div>
                              </div>
                            </td>
                            {entry.days.map((day) => {
                              const { changeclass, availabilityText } =
                                getClassForDay(
                                  day.date,

                                  themeContext?.theme,
                                  entry.employee.id,
                                  entry,
                                );

                              return (
                                <td
                                  key={day.date}
                                  className={clsx({
                                    "availability-today-col":
                                      new Date(day.date).toLocaleDateString(
                                        "en-CA",
                                      ) === todayKey,
                                  })}
                                  style={{ height: "90px" }}
                                >
                                  <div
                                    className={clsx("box-light", changeclass)}
                                    style={{
                                      display: "flex",
                                      flexDirection: "column",
                                      justifyContent: "center",
                                      alignItems: "center",
                                      height: "100%",
                                    }}
                                    onClick={() => {
                                      const now = Date.now();
                                      const timeSinceLastClick =
                                        now - lastClickTimeRef.current;

                                      if (timeSinceLastClick < 300) {
                                        lastClickTimeRef.current = 0;
                                        if (
                                          !isMajster ||
                                          (isMajster &&
                                            entry.employee.id ===
                                              user?.employeeId &&
                                            entry.employee.companyId ===
                                              user?.companyId)
                                        ) {
                                          handleDateClick(
                                            new Date(day.date),
                                            entry.employee.id,
                                            entry.employee.companyId,
                                          );
                                        }
                                      } else {
                                        lastClickTimeRef.current = now;
                                      }
                                    }}
                                  >
                                    {availabilityText ? (
                                      <>
                                        <div className="flex flex-col items-center justify-center text-center">
                                          <div>{`${day.hours.fromHour} - ${day.hours.toHour}`}</div>
                                          {day.hours.duration !== "" && (
                                            <div>{`(${day.hours.duration})`}</div>
                                          )}
                                          <div className="text-xs">
                                            {availabilityText}
                                          </div>
                                        </div>
                                      </>
                                    ) : null}
                                  </div>
                                </td>
                              );
                            })}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
                {entries.length > 0 && (
                  <Pagination
                    itemCount={totalCount}
                    pageIndex={pageIndex}
                    pageSize={pageSize}
                    showInfo
                    showPageSizeSelector
                    showNavigationButtons
                    onPageIndexChange={handlePageClick}
                    onPageSizeChange={setPageSize}
                    allowedPageSizes={[6, 12, 18, 24, 30]}
                  />
                )}
            </>
          </div>
        ) : null}
        <div className="popup">
          <Popup
            className="popup-ava"
            visible={popupVisible}
            onHiding={handleCancel}
            fullScreen={isMobile}
            maxWidth={isMobile ? undefined : 550}
            height={isMobile ? "100%" : "auto"}
            maxHeight={isMobile ? undefined : "80vh"}
            title={t("availabilityPopup.setYourAvailability")}
            showCloseButton
            dragEnabled={!isMobile}
          >
            {onlyWeekendDays ? (
              <div className="p-4 mb-3 rounded-md border-yellow-500 bg-yellow-50 text-yellow-800 text-sm text-justify">
                {t("availabilityPopup.onlyWeekendDaysAlert")}
              </div>
            ) : null}
            <form>
              <Form>
                {isNotEmployee && isFullTime && !isMajster && (
                  <Item
                    dataField={t("availabilityPopup.employee")}
                    editorType="dxTagBox"
                    editorOptions={{
                      items: users,
                      showSelectionControls: true,
                      searchEnabled: true,
                      applyValueMode: "useButtons",
                      showClearButton: true,
                      displayExpr: (user) =>
                        user ? `${user.lastName} ${user.firstName}` : "",
                      valueExpr: "id",
                      value: userSend || [],
                      onValueChanged: (e: any) => setUserSend(e.value || []),
                      placeholder: t("availabilityPopup.selectEmployee"),
                      dropDownOptions: {
                        showScrollbar: "onHover",
                        hideOnOutsideClick: true,
                      },
                    }}
                  />
                )}
                <GroupItem colCount={2}>
                  <Item
                    dataField={t("availabilityPopup.startDate")}
                    editorType="dxDateBox"
                    editorOptions={
                      {
                        value: selectedDate,
                        type: "date",
                        onValueChanged: (e: any) => setSelectedDate(e.value),
                      } as DateBoxTypes.Properties
                    }
                  />
                  <Item
                    dataField={t("availabilityPopup.endDate")}
                    editorType="dxDateBox"
                    editorOptions={
                      {
                        value: selectedEndDate,
                        type: "date",
                        onValueChanged: (e) => setSelectedEndDate(e.value),
                      } as DateBoxTypes.Properties
                    }
                  />
                </GroupItem>
                {timeSlots.map((slot, index) => (
                  <GroupItem
                    colCount={7}
                    key={index + 1}
                    cssClass="group-item-with-time"
                  >
                    <Item
                      dataField={`${t("availabilityPopup.startTime")}${
                        index + 1
                      }`}
                      editorType="dxDateBox"
                      editorOptions={{
                        value: slot.start,
                        onValueChanged: (e: any) => {
                          const newSlots = [...timeSlots];
                          newSlots[index].start = e.value;
                          setTimeSlots(newSlots);
                        },
                        type: "time",
                      }}
                      colSpan={3}
                    />
                    <Item
                      dataField={`${t("availabilityPopup.endTime")} ${
                        index + 1
                      }`}
                      editorType="dxDateBox"
                      editorOptions={{
                        value: slot.end,
                        onValueChanged: (e: any) => {
                          const newSlots = [...timeSlots];
                          newSlots[index].end = e.value;
                          setTimeSlots(newSlots);
                        },
                        type: "time",
                      }}
                      colSpan={3}
                    />
                    <ButtonItem
                      // cssClass="button-tash"
                      horizontalAlignment="left"
                      verticalAlignment="bottom"
                      buttonOptions={{
                        type: "danger",
                        icon: "trash",
                        width: "100%",
                        disabled: timeSlots.length === 1,
                        onClick: () => handleRemoveTimeSlot(index),
                      }}
                      colSpan={1}
                    />
                  </GroupItem>
                ))}
                <ButtonItem
                  // cssClass="button-add"
                  horizontalAlignment="left"
                  buttonOptions={{
                    text: t("availabilityPopup.addTime"),
                    type: "default",
                    onClick: handleAddTimeSlot,
                  }}
                />
                <Item
                  dataField={t("availabilityPopup.notes")}
                  editorType="dxTextArea"
                  editorOptions={{
                    value: notes,
                    onValueChanged: (e: any) => setNotes(e.value),
                    height: 75,
                  }}
                />
                <Item
                  dataField={t("availabilityPopup.excludeHolidays")}
                  editorType="dxCheckBox"
                  label={{
                    visible: false,
                  }}
                  editorOptions={{
                    value: excludeHolidays,
                    onValueChanged: (e: any) => setExcludeHolidays(e.value),
                    text: t("availabilityPopup.excludeHolidays"),
                  }}
                  // cssClass="notes"
                />
                <ButtonItem
                  // cssClass="button-add"
                  horizontalAlignment="left"
                  buttonOptions={{
                    text: t("More options"),
                    type: "default",
                    onClick: handleMoreOptions,
                  }}
                />
                {popupScheduleVisible === true && (
                  <GroupItem cssClass="schedule-change border-[#e0e0e0] dark:border-[#616161]">
                    <GroupItem>
                      <RadioGroup
                        items={triggerOptions}
                        value={triggerType}
                        valueExpr="key"
                        displayExpr="label"
                        onValueChanged={handleTriggerTypeChange}
                        layout="horizontal"
                      />
                    </GroupItem>
                    {triggerType && (
                      <GroupItem>
                        {triggerType === "schedule.daily" && (
                          <GroupItem>
                            <RadioGroup
                              items={triggerOptionsDaily}
                              value={triggerTypeDaily}
                              valueExpr="key"
                              displayExpr="label"
                              onValueChanged={handleTriggerTypeChangeDaily}
                              layout="horizontal"
                            />

                            {triggerTypeDaily === "schedule.what" && (
                              <>
                                <div className="inline-flex items-center space-x-2">
                                  <NumberBox
                                    min={1}
                                    value={numericValue}
                                    width={80}
                                    showSpinButtons={true}
                                    onValueChanged={handleNumericChange}
                                  />
                                  <span>{t("schedule.days")}</span>
                                </div>
                              </>
                            )}
                          </GroupItem>
                        )}
                        {triggerType === "schedule.weekly" && (
                          <GroupItem>
                            <div className="flex items-center space-x-2">
                              <span>{t("schedule.repeatWhat")}</span>
                              <NumberBox
                                min={1}
                                value={numericValue5}
                                width={80}
                                showSpinButtons={true}
                                onValueChanged={handleNumericChange5}
                                className="border border-gray-300 rounded p-1"
                              />
                              <span>{t("schedule.weekNextWeek")}</span>
                            </div>

                            <TagBox
                              items={daysOfWeek}
                              value={selectedDays}
                              displayExpr="name"
                              valueExpr="id"
                              showSelectionControls={true}
                              applyValueMode="useButtons"
                              onValueChanged={handleSelectionChange}
                              placeholder={t("schedule.chooseWeekDay")}
                              className="mt-4"
                            />
                          </GroupItem>
                        )}
                        {triggerType === "schedule.monthly" && (
                          <GroupItem>
                            <RadioGroup
                              items={triggerOptionsMonthly}
                              value={triggerTypeMonthly}
                              valueExpr="key"
                              displayExpr="label"
                              onValueChanged={handleTriggerTypeChangeMonthly}
                              layout="horizontal"
                            />
                            <div className="flex items-center space-x-2">
                              {triggerTypeMonthly === "schedule.monthDay" && (
                                <>
                                  <NumberBox
                                    min={1}
                                    max={31}
                                    value={numericValue1}
                                    width={80}
                                    showSpinButtons={true}
                                    onValueChanged={handleNumericChange1}
                                    className="border border-gray-300 rounded p-1"
                                  />
                                  <span>{t("schedule.what")}</span>
                                  <NumberBox
                                    min={1}
                                    value={numericValue2}
                                    width={80}
                                    showSpinButtons={true}
                                    onValueChanged={handleNumericChange2}
                                    className="border border-gray-300 rounded p-1"
                                  />
                                  <span>{t("schedule.month")}</span>
                                </>
                              )}
                            </div>
                            <div className="flex items-center space-x-2">
                              {triggerTypeMonthly ===
                                "schedule.inTheNextDay" && (
                                <>
                                  <SelectBox
                                    dataSource={daysOptionsInWeek}
                                    valueExpr="value"
                                    displayExpr="label"
                                    value={selectedDayInWeek}
                                    onValueChanged={(e) =>
                                      setSelectedDayInWeek(e.value)
                                    }
                                    className="ml-2 border border-gray-300 rounded p-1 w-40"
                                  />
                                  <SelectBox
                                    dataSource={daysOptions}
                                    valueExpr="value"
                                    displayExpr="label"
                                    value={selectedDay}
                                    onValueChanged={(e) =>
                                      setSelectedDay(e.value)
                                    }
                                    className="ml-2 border border-gray-300 rounded p-1 w-40"
                                  />
                                  <span>{t("schedule.monthWhat")}</span>
                                  <NumberBox
                                    min={1}
                                    value={numericValue3}
                                    width={80}
                                    showSpinButtons={true}
                                    onValueChanged={handleNumericChange3}
                                    className="border border-gray-300 rounded p-1"
                                  />
                                  <span>{t("schedule.month")}</span>
                                </>
                              )}
                            </div>
                          </GroupItem>
                        )}
                        {triggerType === "schedule.yearly" && (
                          <GroupItem cssClass="flex flex-col">
                            <div className="flex items-center space-x-2">
                              <span>{t("schedule.repeatWhat")}</span>
                              <NumberBox
                                min={1}
                                value={numericValue4}
                                width={80}
                                showSpinButtons={true}
                                onValueChanged={handleNumericChange4}
                                className="border border-gray-300 rounded p-1"
                              />
                              <span>{t("schedule.year")}</span>
                            </div>
                            <RadioGroup
                              items={triggerOptionsYearly}
                              value={triggerTypeYearly}
                              valueExpr="key"
                              displayExpr="label"
                              onValueChanged={handleTriggerTypeChangeYearly}
                              layout="horizontal"
                              className="mt-2"
                            />
                            <div className="flex items-center space-x-2">
                              {triggerTypeYearly === "schedule.data" && (
                                <>
                                  <NumberBox
                                    min={1}
                                    max={31}
                                    value={numericValue5}
                                    width={80}
                                    showSpinButtons={true}
                                    onValueChanged={handleNumericChange5}
                                  />
                                  <SelectBox
                                    dataSource={monthOptions}
                                    valueExpr="value"
                                    displayExpr="label"
                                    value={monthSelect}
                                    onValueChanged={(e) =>
                                      setMonthSelect(e.value)
                                    }
                                    className="ml-2 border border-gray-300 rounded p-1 w-40"
                                  />
                                  <span>{t("schedule.month")}</span>
                                </>
                              )}
                            </div>
                            <div className="flex items-center space-x-2">
                              {triggerTypeYearly === "schedule.day" && (
                                <>
                                  <SelectBox
                                    dataSource={daysOptionsInWeek}
                                    valueExpr="value"
                                    displayExpr="label"
                                    value={selectedDayInWeek}
                                    onValueChanged={(e) =>
                                      setSelectedDayInWeek(e.value)
                                    }
                                    className="ml-2 border border-gray-300 rounded p-1 w-40"
                                  />
                                  <SelectBox
                                    dataSource={daysOptions}
                                    valueExpr="value"
                                    displayExpr="label"
                                    value={selectedDay}
                                    onValueChanged={(e) =>
                                      setSelectedDay(e.value)
                                    }
                                    className="ml-2 border border-gray-300 rounded p-1 w-40"
                                  />
                                  <span>{t("schedule.form")}</span>
                                  <SelectBox
                                    dataSource={monthOptions}
                                    valueExpr="value"
                                    displayExpr="label"
                                    value={monthSelect}
                                    onValueChanged={(e) =>
                                      setMonthSelect(e.value)
                                    }
                                    className="ml-2 border border-gray-300 rounded p-1 w-40"
                                  />
                                </>
                              )}
                            </div>
                          </GroupItem>
                        )}
                      </GroupItem>
                    )}
                  </GroupItem>
                )}
              </Form>
            </form>
            {editingExistingDate && viewMode === "Employee" ? (
              <ToolbarItem
                location="before"
                toolbar="bottom"
                widget="dxButton"
                options={{
                  text: t("availabilityPopup.deleteAvailability"),
                  type: "danger",
                  stylingMode: "contained",
                  icon: "trash",
                  onClick: handleDeleteAvailability,
                  disabled: isLoading,
                }}
              />
            ) : null}
            <ToolbarItem
              location="after"
              toolbar="bottom"
              widget="dxButton"
              options={{
                text: t("Send"),
                type: "default",
                stylingMode: "contained",
                onClick: () => {
                  if (viewMode === "Employee") {
                    handleAvailability(user?.employeeId || 0);
                  } else {
                    handleAvailability(entriesEmployeeId || 0);
                  }
                },
                disabled: isLoading || onlyWeekendDays,
              }}
            />
          </Popup>
        </div>
        {isMobile && viewMode === "Employee" && (
          <button
            type="button"
            className="availability-mobile-fab"
            aria-label={t("availabilityPopup.addTime")}
            onClick={() =>
              handleDateClick(
                new Date(),
                user?.employeeId || 0,
                user?.companyId || 0,
              )
            }
          >
            +
          </button>
        )}
        {!(isMobile && viewMode === "Employee") &&
        (viewMode === "Employee" ||
          (viewMode === "AllEmployees" && entries.length > 0)) ? (
          <div className="flex flex-wrap justify-center gap-4 pt-6">
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 bg-[#a3e635] availability-legend-swatch"></div>
              <div className="leading-none">{t("Holiday")}</div>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 bg-[#4d7c0f] availability-legend-swatch"></div>
              <div className="leading-none">{t("Day off")}</div>
            </div>
            <div className="leading-none">{t("* Reported availability")}</div>
          </div>
        ) : null}
      </div>
    </ScrollView>
  );
};
